import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, CreditCard, 
  Plus, Pencil, Trash2, ChevronRight, LogOut, Menu, X,
  TrendingUp, DollarSign, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-emerald-100 text-emerald-700',
  preparing: 'bg-blue-100 text-blue-700',
  ready: 'bg-purple-100 text-purple-700',
  delivered: 'bg-stone-100 text-stone-600',
  cancelled: 'bg-red-100 text-red-700',
  active: 'bg-emerald-100 text-emerald-700'
};

const statusLabels = {
  pending: 'Pendiente',
  paid: 'Pagado',
  preparing: 'Preparando',
  ready: 'Listo',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
  active: 'Activa'
};

export default function AdminPanel() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Product form
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category_id: '',
    image_url: '', weight: '', temperature: '', stock: 100
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!user || user.role !== 'admin') return;
      
      try {
        const [statsRes, productsRes, catsRes, ordersRes, subsRes, usersRes] = await Promise.all([
          fetch(`${API}/admin/stats`, { credentials: 'include' }),
          fetch(`${API}/products`),
          fetch(`${API}/categories`),
          fetch(`${API}/admin/orders`, { credentials: 'include' }),
          fetch(`${API}/admin/subscriptions`, { credentials: 'include' }),
          fetch(`${API}/admin/users`, { credentials: 'include' })
        ]);

        if (statsRes.ok) setStats(await statsRes.json());
        if (productsRes.ok) setProducts(await productsRes.json());
        if (catsRes.ok) setCategories(await catsRes.json());
        if (ordersRes.ok) setOrders(await ordersRes.json());
        if (subsRes.ok) setSubscriptions(await subsRes.json());
        if (usersRes.ok) setUsers(await usersRes.json());
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSaveProduct = async () => {
    try {
      const url = editingProduct 
        ? `${API}/admin/products/${editingProduct.product_id}`
        : `${API}/admin/products`;
      
      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...productForm,
          price: parseFloat(productForm.price),
          stock: parseInt(productForm.stock)
        })
      });

      if (response.ok) {
        toast.success(editingProduct ? 'Producto actualizado' : 'Producto creado');
        setProductDialogOpen(false);
        setEditingProduct(null);
        setProductForm({ name: '', description: '', price: '', category_id: '', image_url: '', weight: '', temperature: '', stock: 100 });
        // Refresh products
        const prodRes = await fetch(`${API}/products`);
        if (prodRes.ok) setProducts(await prodRes.json());
      } else {
        throw new Error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar el producto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('¿Eliminar este producto?')) return;
    
    try {
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Producto eliminado');
        setProducts(products.filter(p => p.product_id !== productId));
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API}/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Estado actualizado');
        setOrders(orders.map(o => 
          o.order_id === orderId ? { ...o, status: newStatus } : o
        ));
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category_id: product.category_id,
      image_url: product.image_url || '',
      weight: product.weight || '',
      temperature: product.temperature || '',
      stock: product.stock || 100
    });
    setProductDialogOpen(true);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'products', label: 'Productos', icon: Package },
    { id: 'orders', label: 'Pedidos', icon: ShoppingCart },
    { id: 'subscriptions', label: 'Suscripciones', icon: CreditCard },
    { id: 'users', label: 'Usuarios', icon: Users }
  ];

  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-panel">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-stone-200 p-4 z-50 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)}>
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-bold text-stone-900">Admin Panel</span>
        <div></div>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-stone-200 z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Logo"
              className="h-10 w-auto"
            />
          </div>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="p-4 space-y-1">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                activeTab === item.id
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'text-stone-600 hover:bg-stone-50'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-stone-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-stone-600 hover:bg-stone-50 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-20 lg:pt-8 p-4 lg:p-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-stone-900">Dashboard</h1>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl p-6 border border-stone-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-700" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-stone-900">${stats.total_revenue?.toFixed(0) || 0}</p>
                <p className="text-sm text-stone-500">Ingresos Totales</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-stone-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-stone-900">{stats.total_orders || 0}</p>
                <p className="text-sm text-stone-500">Pedidos Totales</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-stone-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-stone-900">{stats.total_subscriptions || 0}</p>
                <p className="text-sm text-stone-500">Suscripciones Activas</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-stone-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-stone-900">{stats.total_users || 0}</p>
                <p className="text-sm text-stone-500">Usuarios</p>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-2xl border border-stone-200">
              <div className="p-6 border-b border-stone-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-stone-900">Pedidos Recientes</h2>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab('orders')}>
                  Ver todos <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="divide-y divide-stone-100">
                {orders.slice(0, 5).map(order => (
                  <div key={order.order_id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-900">#{order.order_id.slice(-8).toUpperCase()}</p>
                      <p className="text-sm text-stone-500">{order.user_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-stone-900">${order.total.toFixed(2)}</p>
                      <Badge className={`${statusColors[order.status]} border-0`}>
                        {statusLabels[order.status]}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-stone-900">Productos</h1>
              <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-emerald-700 hover:bg-emerald-800 rounded-full"
                    onClick={() => {
                      setEditingProduct(null);
                      setProductForm({ name: '', description: '', price: '', category_id: '', image_url: '', weight: '', temperature: '', stock: 100 });
                    }}
                    data-testid="add-product-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Producto
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <label className="text-sm font-medium text-stone-700">Nombre</label>
                      <Input
                        value={productForm.name}
                        onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                        className="mt-1"
                        data-testid="product-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-stone-700">Descripción</label>
                      <Input
                        value={productForm.description}
                        onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-stone-700">Precio</label>
                        <Input
                          type="number"
                          value={productForm.price}
                          onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                          className="mt-1"
                          data-testid="product-price-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-stone-700">Stock</label>
                        <Input
                          type="number"
                          value={productForm.stock}
                          onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-stone-700">Categoría</label>
                      <Select
                        value={productForm.category_id}
                        onValueChange={(value) => setProductForm({...productForm, category_id: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.category_id} value={cat.category_id}>
                              {cat.name_es}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-stone-700">URL de Imagen</label>
                      <Input
                        value={productForm.image_url}
                        onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-stone-700">Peso</label>
                        <Input
                          value={productForm.weight}
                          onChange={(e) => setProductForm({...productForm, weight: e.target.value})}
                          className="mt-1"
                          placeholder="ej: 200 gr"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-stone-700">Temperatura</label>
                        <Select
                          value={productForm.temperature}
                          onValueChange={(value) => setProductForm({...productForm, temperature: value})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Frío">Frío</SelectItem>
                            <SelectItem value="Caliente">Caliente</SelectItem>
                            <SelectItem value="Temp. Amb.">Temp. Ambiente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-emerald-700 hover:bg-emerald-800"
                      onClick={handleSaveProduct}
                      data-testid="save-product-btn"
                    >
                      {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Producto</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Categoría</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Precio</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Stock</th>
                      <th className="text-right p-4 text-sm font-medium text-stone-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {products.map(product => (
                      <tr key={product.product_id} className="hover:bg-stone-50" data-testid={`admin-product-${product.product_id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-stone-100">
                              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                            </div>
                            <div>
                              <p className="font-medium text-stone-900">{product.name}</p>
                              <p className="text-sm text-stone-500 truncate max-w-xs">{product.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="secondary" className="bg-stone-100 text-stone-600 border-0">
                            {categories.find(c => c.category_id === product.category_id)?.name_es || 'N/A'}
                          </Badge>
                        </td>
                        <td className="p-4 font-medium text-stone-900">${product.price.toFixed(2)}</td>
                        <td className="p-4 text-stone-600">{product.stock}</td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditProduct(product)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteProduct(product.product_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-stone-900">Pedidos</h1>
            
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Pedido</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Cliente</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Tipo</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Total</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Estado</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Fecha</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {orders.map(order => (
                      <tr key={order.order_id} className="hover:bg-stone-50" data-testid={`admin-order-${order.order_id}`}>
                        <td className="p-4 font-medium text-stone-900">#{order.order_id.slice(-8).toUpperCase()}</td>
                        <td className="p-4">
                          <p className="text-stone-900">{order.user_name}</p>
                          <p className="text-sm text-stone-500">{order.user_email}</p>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className="border-stone-200">
                            {order.order_type === 'reservation' ? 'Apartado' : 'Inmediato'}
                          </Badge>
                        </td>
                        <td className="p-4 font-medium text-stone-900">${order.total.toFixed(2)}</td>
                        <td className="p-4">
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleUpdateOrderStatus(order.order_id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusLabels).map(([key, label]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4 text-stone-500 text-sm">
                          {format(new Date(order.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-stone-900">Suscripciones</h1>
            
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Usuario</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Plan</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Comidas</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Estado</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Vence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {subscriptions.map(sub => (
                      <tr key={sub.subscription_id} className="hover:bg-stone-50">
                        <td className="p-4 text-stone-900">{sub.user_email}</td>
                        <td className="p-4 font-medium text-stone-900">{sub.plan_name}</td>
                        <td className="p-4 text-stone-600">{sub.meals_remaining}/{sub.meals_per_month}</td>
                        <td className="p-4">
                          <Badge className={`${statusColors[sub.status]} border-0`}>
                            {statusLabels[sub.status]}
                          </Badge>
                        </td>
                        <td className="p-4 text-stone-500 text-sm">
                          {sub.end_date ? format(new Date(sub.end_date), 'dd/MM/yyyy', { locale: es }) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-stone-900">Usuarios</h1>
            
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Usuario</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Email</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Rol</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Registro</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {users.map(u => (
                      <tr key={u.user_id} className="hover:bg-stone-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                              {u.picture ? (
                                <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                              ) : (
                                <Users className="w-5 h-5 text-emerald-700" />
                              )}
                            </div>
                            <span className="font-medium text-stone-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-stone-600">{u.email}</td>
                        <td className="p-4">
                          <Badge className={u.role === 'admin' ? 'bg-purple-100 text-purple-700 border-0' : 'bg-stone-100 text-stone-600 border-0'}>
                            {u.role === 'admin' ? 'Administrador' : 'Cliente'}
                          </Badge>
                        </td>
                        <td className="p-4 text-stone-500 text-sm">
                          {u.created_at ? format(new Date(u.created_at), 'dd/MM/yyyy', { locale: es }) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
